import { useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useAgentePermissions } from "@/hooks/useAgentePermissions";
import { useCurrentAgenteStore } from "@/stores/currentAgenteStore";
import { autenticacaoService } from "@/services/autenticacaoService";
import { AppConfig } from "@/datasources/AppConfig";

interface GestorRouteProps {
  children: React.ReactNode;
}

/**
 * Guarda de rota: só permite acesso se o usuário estiver autenticado e o
 * agente atual tiver `gestor === true` — flag que vem da API
 * (`GET /v1/ouvidorias/{id}/agentes/{id}`), não de storage local.
 *
 * Sem contexto de agente (agenteId/ouvidoriaId ausentes), trata como não-gestor.
 * Enquanto o agente está carregando, renderiza nada para evitar flicker.
 */
export function GestorRoute({ children }: GestorRouteProps) {
  const { isGestor } = useAgentePermissions();
  const hasFetched = useCurrentAgenteStore((s) => s.hasFetched);
  const fetchCurrentAgente = useCurrentAgenteStore((s) => s.fetchCurrentAgente);

  const autenticado =
    autenticacaoService.estaAutenticado() &&
    !autenticacaoService.tokenExpirado();
  const temContextoAgente = Boolean(AppConfig.agenteId && AppConfig.ouvidoriaId);

  useEffect(() => {
    if (autenticado && temContextoAgente && !hasFetched) {
      void fetchCurrentAgente();
    }
  }, [autenticado, temContextoAgente, hasFetched, fetchCurrentAgente]);

  if (!autenticado) return <Navigate to="/login" replace />;
  // Sem agente vinculado não há como ser gestor — volta para a visão geral
  if (!temContextoAgente) return <Navigate to="/dashboard" replace />;
  // Bloqueia só na primeira carga. Durante refetch (isLoading), o agente
  // continua válido — desmontar os filhos aqui causaria loop de remontagem,
  // pois o DashboardLayout força fetchCurrentAgente(true) a cada mount.
  if (!hasFetched) return null;
  if (!isGestor) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}
